import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { DollarSign, MapPin, Send } from "lucide-react";
import { Input } from "../ui/input";
import {
  getCustomerandVendorPaymentList,
  getAccounChargesList,
  GetAddressBookAccountVendorDetails,
  GetAccountAdressbookModuleList,
  GetAccountAdressbookBasisList,
} from "@/redux/Common/actions";
import { useDispatch, useSelector } from "react-redux";
import {
  CreateAddressBookAccount,
  getAddressByID,
  GetAddressBookAccountByID,
  DeleteAccountChargeById,
} from "@/redux/Address/actions";
import CelebrationButton from "../ui/CelebrationButton/CelebrationButton";
import { showToast } from "@/redux/Toast/actions";
import { callLoader } from "@/redux/Loader/actions";
import SectionHeader from "./SectionHeader";

const AddressBookAccount = ({
  bookingData,
  onToggle,
  expandedSections,   // this is an object, e.g. { s1: true, s2: false, s3: true }
  standardTermsList,
  activeTab,
}) => {
  const inputClass =
    "w-full px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all";

  const dispatch = useDispatch();
  const { isAnimate } = useSelector((state) => state.toast);

  const [basisList, setBasisList] = useState([]);
  const [moduleList, setModuleList] = useState([]);
  const [descriptionData, setDescriptionData] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [activeRow, setActiveRow] = useState(null);
  const [isAccountingLoaded, setIsAccountingLoaded] = useState(false);
  const [activeField, setActiveField] = useState(null);
  const [vendorSuggestions, setVendorSuggestions] = useState([]);
  const [charges, setCharges] = useState([]);

  const [AccountformData, setAccountFormData] = useState({
    ddlvendpayTerms: "",
    ddlcustpayTerms: "",
    customerCreditLimit: "",
    vendorCreditAmount: "",
    activeRates: false,
    customerOnHold: false,
  });

  // ── Autocomplete ──────────────────────────────────────────
  const handleAutocompleteChange = async (index, field, value) => {
    const updated = [...charges];
    updated[index][field] = value;
    setCharges(updated);
    setActiveRow(index);
    setActiveField(field);

    if (!value || value.trim().length < 2) {
      setSuggestions([]);
      setVendorSuggestions([]);
      return;
    }

    if (field === "description") {
      try {
        const res = await dispatch(getAccounChargesList(value.trim()));
        setSuggestions(res || []);
      } catch (err) {
        console.error("Charge search failed", err);
        setSuggestions([]);
      }
    } else if (field === "vendor") {
      try {
        const res = await dispatch(GetAddressBookAccountVendorDetails(value.trim()));
        setVendorSuggestions(res || []);
      } catch (err) {
        console.error("Vendor search failed", err);
        setVendorSuggestions([]);
      }
    }
  };

  const handleSelect = (index, field, item) => {
    const updated = [...charges];
    if (field === "description") {
      updated[index].description = item.chargesdescription;
      updated[index].descriptionId = item.descriptionId;
      updated[index].descriptioncode = item.descriptioncode;
    } else {
      updated[index].vendor = item.vendorName;
      updated[index].vendorId = item.vendorId;
    }
    setCharges(updated);
    setSuggestions([]);
    setVendorSuggestions([]);
    setActiveRow(null);
    setActiveField(null);
  };

  // ── Effects ───────────────────────────────────────────────
  useEffect(() => {
    const fetchPaymentTerms = async () => {
      try {
        const terms = await dispatch(getCustomerandVendorPaymentList());
        setAccountFormData(terms);
      } catch (error) {
        console.error("Error fetching payment terms:", error);
      }
    };
    fetchPaymentTerms();
  }, [bookingData]);

  useEffect(() => {
    if (isAccountingLoaded) return;
    if (!moduleList.length || !basisList.length) return;
    setCharges(
      Array.from({ length: 5 }, () => ({
        description: "",
        basis: basisList[0]?.value || "",
        vendor: "",
        sell: "0.00",
        buy: "0.00",
        module: moduleList[0]?.value || "",
      }))
    );
  }, [moduleList, basisList, isAccountingLoaded]);

  useEffect(() => {
    if (activeRow !== null && activeField === "description") {
      setSuggestions(descriptionData || []);
    }
  }, [descriptionData]);

  useEffect(() => {
    const fetchModules = async () => {
      try {
        const res = await dispatch(GetAccountAdressbookModuleList());
        setModuleList(res || []);
      } catch (err) {
        console.error("Module fetch failed", err);
        setModuleList([]);
      }
    };
    fetchModules();
  }, []);

  useEffect(() => {
    const fetchBasisList = async () => {
      try {
        const res = await dispatch(GetAccountAdressbookBasisList());
        if (res && Array.isArray(res)) setBasisList(res);
      } catch (err) {
        console.error("Failed to fetch basis list:", err);
      }
    };
    fetchBasisList();
  }, [dispatch]);

  useEffect(() => {
    const loadAccountingData = async () => {
      if (activeTab !== "accounting") return;
      dispatch(callLoader(1));
      try {
        const addressData = await dispatch(getAddressByID(bookingData));
        const companyId = addressData?._Company?.id;
        if (!companyId) return;

        const res = await dispatch(GetAddressBookAccountByID(companyId));
        const data = res;
        if (!data) return;

        setIsAccountingLoaded(true);

        setAccountFormData({
          ddlcustpayTerms: data.accountData.customerPaymentTerms || "",
          ddlvendpayTerms: data.accountData.vendorPaymentTerms || "",
          customerCreditLimit: data.accountData.customerCreditLimit || "",
          vendorCreditAmount: data.accountData.vendorCreditAmount || "",
          activeRates: !!data.accountData.activeRates,
          customerOnHold: !!data.accountData.customerOnHold,
        });

        setCharges(normalizeCharges(data.charges));
      } catch (err) {
        console.error("Accounting load failed:", err);
      } finally {
        dispatch(callLoader(0));
      }
    };
    loadAccountingData();
  }, [activeTab, bookingData, dispatch]);

  // ── Helpers ───────────────────────────────────────────────
  const normalizeCharges = (dbCharges = []) => {
    const rows = dbCharges.map((row) => ({
      id: row.id,
      descriptionId: row.descriptionId,
      description: row.description,
      descriptioncode: row.descriptionCode,
      basis: row.basis,
      sell: row.sell,
      buy: row.buy,
      vendor: row.vendor,
      vendorId: row.vendorId,
      module: row.division,
    }));
    while (rows.length < 5) {
      rows.push({
        description: "",
        basis: basisList[0]?.value || "",
        vendor: "",
        sell: "0.00",
        buy: "0.00",
        module: moduleList[0]?.value || "",
      });
    }
    return rows;
  };

  const AccounthandleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setAccountFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const tablehandleChange = (index, field, value) => {
    const updated = [...charges];
    updated[index][field] = value;
    setCharges(updated);
  };

  const handleAddRow = () => {
    setCharges([
      ...charges,
      {
        description: "",
        basis: basisList[0]?.value || "",
        vendor: "",
        sell: "0.00",
        buy: "0.00",
        module: moduleList[0]?.value || "",
      },
    ]);
  };

  const handleDeleteRow = async (index) => {
    const row = charges[index];
    if (!window.confirm("Are you sure you want to delete this charge?")) return;
    if (row?.id) {
      await DeleteAccountChargeById(row.id);
    }
    setCharges((prev) => prev.filter((item) => item !== row));
  };

  const handleSave = async () => {
    const filledCharges = charges
      .filter((row) => row.description && row.description.trim() !== "")
      .map((row, index) => ({
        id: row.id || index + 1,
        descriptionId: Number(row.descriptionId) || 0,
        description: row.description,
        descriptioncode: row.descriptioncode,
        basis: row.basis,
        sell: Number(row.sell) || 0,
        buy: Number(row.buy) || 0,
        vendor: row.vendor,
        vendorId: row.vendorId ? Number(row.vendorId) : 0,
        division: row.module,
      }));

    if (filledCharges.length === 0) {
      dispatch(showToast("Please enter at least one charge description to save.", "error"));
      return;
    }

    const resultData = await dispatch(getAddressByID(bookingData));
    const payload = {
      accountData: {
        companyID: resultData?._Company?.id || 0,
        customerCreditLimit: AccountformData.customerCreditLimit || "0",
        vendorCreditAmount: AccountformData.vendorCreditAmount || "0",
        customerPaymentTerms: AccountformData.ddlcustpayTerms
          ? Number(AccountformData.ddlcustpayTerms)
          : 0,
        vendorPaymentTerms: AccountformData.ddlvendpayTerms
          ? Number(AccountformData.ddlvendpayTerms)
          : 0,
        activeRates: !!AccountformData.activeRates,
        customerOnHold: !!AccountformData.customerOnHold,
        modifiedBy: "admin",
      },
      charges: filledCharges,
    };

    try {
      const AccDetails = await CreateAddressBookAccount(payload);
      if (AccDetails?.statuscode === 200 || AccDetails?.statuscode === 201) {
        dispatch(showToast("Record saved successfully", "success", true));
      }
    } catch (err) {
      console.error(err);
      dispatch(showToast("Error saving data.", "error"));
    }
  };

  // ── Render ────────────────────────────────────────────────
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-6 space-y-6 pb-12"
    >
      {/* ── Accounting Details ── */}
      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden">
        <SectionHeader
          icon={MapPin}
          title="Accounting Details"
          color="bg-emerald-600"
          section="s2"
          expandedSections={expandedSections}
          toggleSection={onToggle}
        />

        {/* Use object property access: expandedSections?.["s2"] */}
        {expandedSections?.["s2"] && (
          <div className="p-4 border-t border-slate-200 dark:border-slate-800">
            <div className="grid grid-cols-4 gap-6 items-start">
              {/* Customer Payment Terms */}
              <div className="grid grid-cols-12 items-center gap-2">
                <label className="col-span-12 text-xs font-medium text-slate-600 dark:text-slate-400">
                  Customer Payment Terms:
                </label>
                <div className="col-span-12">
                  <select
                    className="w-full p-2 text-sm border border-slate-300 dark:border-slate-700 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                    name="ddlcustpayTerms"
                    value={AccountformData?.ddlcustpayTerms}
                    onChange={AccounthandleInputChange}
                  >
                    {standardTermsList?.map((terms, key) => (
                      <option key={key} value={terms.termsID}>
                        {terms.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Customer Credit Limit */}
              <div className="grid grid-cols-12 items-center gap-2">
                <label className="col-span-12 text-xs font-medium text-slate-600 dark:text-slate-400">
                  Customer Credit Limit: <span className="text-red-500">*</span>
                </label>
                <div className="col-span-12">
                  <Input
                    type="text"
                    className={inputClass}
                    value={AccountformData.customerCreditLimit ? `$${AccountformData.customerCreditLimit}` : ""}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9.]/g, "");
                      setAccountFormData({ ...AccountformData, customerCreditLimit: value });
                    }}
                  />
                </div>
              </div>

              {/* Vendor Payment Terms */}
              <div className="grid grid-cols-12 items-center gap-2">
                <label className="col-span-12 text-xs font-medium text-slate-600 dark:text-slate-400">
                  Vendor Payment Terms:
                </label>
                <div className="col-span-12">
                  <select
                    className="w-full p-2 border text-sm border-slate-300 dark:border-slate-700 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                    name="ddlvendpayTerms"
                    value={AccountformData?.ddlvendpayTerms}
                    onChange={AccounthandleInputChange}
                  >
                    {standardTermsList?.map((terms, key) => (
                      <option key={key} value={terms.termsID}>
                        {terms.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Vendor Credit Amount */}
              <div className="grid grid-cols-12 items-center gap-2">
                <label className="col-span-12 text-xs font-medium text-slate-600 dark:text-slate-400">
                  Vendor Credit Amount: <span className="text-red-500">*</span>
                </label>
                <div className="col-span-12">
                  <Input
                    type="text"
                    className={inputClass}
                    value={AccountformData.vendorCreditAmount ? `$${AccountformData.vendorCreditAmount}` : ""}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9.]/g, "");
                      setAccountFormData({ ...AccountformData, vendorCreditAmount: value });
                    }}
                  />
                </div>
              </div>

              {/* Active Rates */}
              <div className="grid grid-cols-12 items-center gap-2">
                <label className="col-span-12 text-xs font-medium text-slate-600 dark:text-slate-400">
                  Active Rates:
                </label>
                <div className="col-span-12">
                  <Input
                    type="checkbox"
                    className="w-4 h-4"
                    name="activeRates"
                    checked={AccountformData.activeRates || false}
                    onChange={AccounthandleInputChange}
                  />
                </div>
              </div>

              {/* Customer on Hold */}
              <div className="grid grid-cols-12 items-center gap-2">
                <label className="col-span-12 text-xs font-medium text-slate-600 dark:text-slate-400">
                  Customer on Hold:
                </label>
                <div className="col-span-12">
                  <Input
                    type="checkbox"
                    className="w-4 h-4"
                    name="customerOnHold"
                    checked={AccountformData.customerOnHold || false}
                    onChange={AccounthandleInputChange}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Additional Charges ── */}
      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden">
        <SectionHeader
          icon={DollarSign}
          title="Additional Charges"
          color="bg-amber-600"
          section="s3"
          expandedSections={expandedSections}
          toggleSection={onToggle}
        />

        {expandedSections?.["s3"] && (
          <div className="bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700">
            <div className="overflow-x-auto max-h-[520px] overflow-y-auto">
              <table className="w-full text-[12.5px]">
                {/* Head */}
                <thead className="sticky top-0 z-10 bg-slate-100/80 dark:bg-slate-800/70 backdrop-blur text-[10.5px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
                  <tr>
                    <th className="text-left font-semibold px-5 py-3">Charges Description</th>
                    <th className="text-left font-semibold px-3 py-3">Basis</th>
                    <th className="text-left font-semibold px-3 py-3">Vendor</th>
                    <th className="text-center font-semibold px-3 py-3">Sell</th>
                    <th className="text-center font-semibold px-3 py-3">Buy</th>
                    <th className="text-center font-semibold px-3 py-3">Module</th>
                    <th className="text-center font-semibold px-3 py-3">Del</th>
                  </tr>
                </thead>

                {/* Body */}
                <tbody>
                  {charges.map((row, index) => (
                    <tr
                      key={index}
                      className={[
                        "border-t border-slate-100 dark:border-slate-700/50",
                        "transition-colors hover:bg-blue-50/30 dark:hover:bg-blue-900/10",
                        index % 2 === 1
                          ? "bg-slate-50/60 dark:bg-slate-800/30"
                          : "bg-white dark:bg-slate-900",
                      ].join(" ")}
                    >
                      {/* Description */}
                      <td className="px-5 py-2.5 relative">
                        <input
                          type="text"
                          className="w-full min-w-[160px] h-7 border border-slate-200 dark:border-slate-600 rounded-lg px-2.5 bg-white dark:bg-slate-800 text-[12px] text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-blue-400 transition-shadow"
                          value={row.description}
                          onChange={(e) => handleAutocompleteChange(index, "description", e.target.value)}
                        />
                        {activeRow === index && activeField === "description" && suggestions.length > 0 && (
                          <ul className="absolute top-full left-5 right-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg shadow-lg max-h-40 overflow-y-auto z-20 py-1">
                            {suggestions.map((item) => (
                              <li
                                key={item.descriptioncode}
                                className="px-3 py-1.5 text-[12px] text-slate-700 dark:text-slate-300 hover:bg-blue-50 dark:hover:bg-blue-900/30 cursor-pointer"
                                onClick={() => handleSelect(index, "description", item)}
                              >
                                {item.chargesdescription}
                              </li>
                            ))}
                          </ul>
                        )}
                      </td>

                      {/* Basis */}
                      <td className="px-3 py-2.5">
                        <select
                          className="w-full min-w-[90px] h-7 border border-slate-200 dark:border-slate-600 rounded-lg px-2 text-[12px] bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-1 focus:ring-blue-400 transition-shadow"
                          value={row.basis}
                          onChange={(e) => tablehandleChange(index, "basis", e.target.value)}
                        >
                          {basisList.map((opt) => (
                            <option key={opt.id} value={opt.name}>
                              {opt.name}
                            </option>
                          ))}
                        </select>
                      </td>

                      {/* Vendor */}
                      <td className="px-3 py-2.5 relative">
                        <input
                          type="text"
                          className="w-full min-w-[130px] h-7 border border-slate-200 dark:border-slate-600 rounded-lg px-2.5 bg-white dark:bg-slate-800 text-[12px] text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-blue-400 transition-shadow"
                          value={row.vendor}
                          onChange={(e) => handleAutocompleteChange(index, "vendor", e.target.value)}
                        />
                        {activeRow === index && activeField === "vendor" && vendorSuggestions.length > 0 && (
                          <ul className="absolute top-full left-3 right-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg shadow-lg max-h-40 overflow-y-auto z-20 py-1">
                            {vendorSuggestions.map((item) => (
                              <li
                                key={item.vendorId}
                                className="px-3 py-1.5 text-[12px] text-slate-700 dark:text-slate-300 hover:bg-blue-50 dark:hover:bg-blue-900/30 cursor-pointer"
                                onClick={() => handleSelect(index, "vendor", item)}
                              >
                                {item.vendorName}
                              </li>
                            ))}
                          </ul>
                        )}
                      </td>

                      {/* Sell */}
                      <td className="px-3 py-2.5 text-center">
                        <input
                          type="text"
                          className="w-full h-7 border border-slate-200 dark:border-slate-600 rounded-lg px-2 text-[12px] text-center tabular-nums bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-1 focus:ring-blue-400 transition-shadow"
                          value={row.sell}
                          onChange={(e) => tablehandleChange(index, "sell", e.target.value)}
                        />
                      </td>

                      {/* Buy */}
                      <td className="px-3 py-2.5 text-center">
                        <input
                          type="text"
                          className="w-full h-7 border border-slate-200 dark:border-slate-600 rounded-lg px-2 text-[12px] text-center tabular-nums bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-1 focus:ring-blue-400 transition-shadow"
                          value={row.buy}
                          onChange={(e) => tablehandleChange(index, "buy", e.target.value)}
                        />
                      </td>

                      {/* Module */}
                      <td className="px-3 py-2.5">
                        <select
                          className="w-full min-w-[110px] h-7 border border-slate-200 dark:border-slate-600 rounded-lg px-2 text-[12px] bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-1 focus:ring-blue-400 transition-shadow"
                          value={row.module}
                          onChange={(e) => tablehandleChange(index, "module", e.target.value)}
                        >
                          <option value="">-- Select Module --</option>
                          {moduleList.map((opt) => (
                            <option key={opt.id} value={opt.id}>
                              {opt.divisionName}
                            </option>
                          ))}
                        </select>
                      </td>

                      {/* Delete */}
                      <td className="px-3 py-2.5 text-center">
                        <button
                          type="button"
                          onClick={() => handleDeleteRow(index)}
                          className="inline-flex items-center justify-center w-6 h-6 rounded-full text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors text-[13px] font-bold"
                          title="Delete row"
                        >
                          ✕
                        </button>
                      </td>
                    </tr>
                  ))}

                  {/* Add Row */}
                  <tr className="border-t border-slate-100 dark:border-slate-700/50 bg-slate-50/40 dark:bg-slate-800/20">
                    <td className="px-5 py-3" colSpan={7}>
                      <button
                        type="button"
                        onClick={handleAddRow}
                        className="inline-flex items-center gap-1.5 h-7 px-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-[11.5px] font-semibold transition-colors shadow-sm"
                      >
                        + Add More Charges
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Save Button */}
            <div className="px-5 py-4 border-t border-slate-100 dark:border-slate-700/50 flex justify-center bg-slate-50/50 dark:bg-slate-800/20">
              <CelebrationButton
                onClick={() => handleSave()}
                label="Submit / Update"
                className="bg-green-600 hover:bg-green-700 text-white px-5 py-2 rounded-lg text-sm font-semibold transition-colors shadow-sm"
                icon={<Send className="w-4 h-4 mr-2" />}
                isSuccess={isAnimate}
              />
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default AddressBookAccount;